import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding } from '@vibesites/runtime/react';
import pagesData from '../pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const pages = useMemo(
    () =>
      Object.values(pagesData as Record<string, PageEntry>)
        .filter((p) => {
          if (p.path === '/sitemap' || p.path.includes('{{')) return false;
          if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
          return true;
        })
        .sort((a, b) => a.position - b.position),
    [hasVirtualTours],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="sitemapcontent.heading"
          as="h1"
          className="font-heading text-3xl font-normal text-foreground mb-10 text-center"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-[var(--vibe-color-text)] rounded px-6 py-4 text-center font-body text-sm text-foreground hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] hover:border-[var(--vibe-color-text-on-primary)] transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Sitemap',
    },
  },
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
